import type { HotspotScene, HotspotSceneBlock, LocaleCode } from '@fixit/bozp-content';
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { resolveLocalized } from '../locale';
import { SCENE_CSS } from './animation/css';
import { Blueprint, blueprintIds, Dim, Solid } from './animation/scenes-shared';

/**
 * Self-contained illustrated scenes for hotspot blocks: inline SVG, zero
 * remote assets (unlike hotspotImage there is nothing to 404). Static
 * technical safety diagrams built from the shared second-generation
 * vocabulary (`animation/scenes-shared`), so they read as one system with the
 * animation scenes - minus the step choreography. The numbered pins are
 * positioned by the content's `hotspots[].x/y` fractions, so every feature a
 * pin names sits under that fraction of the 480×280 viewBox.
 */

/** Full-body harness from behind - EN 361 attachment points. Pins: dorsal ring (0.5, 0.36), thigh straps (0.5, 0.71). */
function HarnessPointsArt({ en }: { en: boolean }) {
  const ids = blueprintIds('harnessPoints');
  return (
    <Blueprint
      id="harnessPoints"
      label={en ? 'Full-body harness - attachment points' : 'Zachycovací postroj - kotvicí body'}
      title={en ? 'Full-body harness · EN 361' : 'Zachycovací postroj · EN 361'}
      source="ČSN EN 361 · NV 362/2005 Sb., příl. čl. II"
    >
      {/* ground under the feet */}
      <Solid hatch={ids.hatch} x={150} y={246} width={180} height={10} edge="top" />
      {/* anchor beam top right + lanyard rising from the dorsal ring */}
      <Solid hatch={ids.hatch} x={300} y={32} width={60} height={9} edge="bottom" />
      <circle className="hbB-draw" cx="318" cy="47" r="5" />
      <path className="hbB-ghost" d="M240 92 q22 -40 76 -46" />
      <text className="hbB-soft" x="366" y="52">
        {en ? 'anchor' : 'kotvení'}
      </text>
      {/* figure seen from the back */}
      <g className="hbB-draw">
        <circle cx="240" cy="44" r="15" />
        <line x1="240" y1="59" x2="240" y2="70" />
        <path d="M204 70 H276 L270 168 H210 Z" />
        <line x1="204" y1="72" x2="184" y2="150" />
        <line x1="276" y1="72" x2="296" y2="150" />
        <circle cx="183" cy="156" r="5" />
        <circle cx="297" cy="156" r="5" />
        <path d="M212 168 L205 246 M234 168 L231 246 M246 168 L249 246 M268 168 L275 246" />
      </g>
      {/* harness webbing: shoulder straps crossing at the dorsal ring, waist belt, sub-pelvic and thigh straps */}
      <g className="hbB-draw-heavy">
        <path d="M214 72 L266 130 M266 72 L214 130" />
        <path d="M214 130 L216 142 M266 130 L264 142" />
        <line x1="208" y1="142" x2="272" y2="142" />
        <path d="M222 142 V193 M258 142 V193" />
        <path d="M207 199 a14 6 0 1 0 28 0 a14 6 0 1 0 -28 0" />
        <path d="M245 199 a14 6 0 1 0 28 0 a14 6 0 1 0 -28 0" />
        <line x1="235" y1="199" x2="245" y2="199" />
      </g>
      {/* dorsal D-ring (attachment point "A") at the strap crossing */}
      <circle className="hbB-draw-heavy" cx="240" cy="101" r="9" />
      <circle className="hbB-ink" cx="240" cy="101" r="3" />
      <circle className="hbB-focus" cx="240" cy="101" r="17" />
      {/* leaders + labels */}
      <line className="hbB-leader" x1="220" y1="101" x2="146" y2="101" />
      <text className="hbB-lbl" x="140" y="106" textAnchor="end">
        {en ? 'dorsal point A' : 'zádový bod A'}
      </text>
      <line className="hbB-leader" x1="262" y1="80" x2="314" y2="72" />
      <text className="hbB-soft" x="318" y="76">
        {en ? 'shoulder straps' : 'ramenní popruhy'}
      </text>
      <line className="hbB-leader" x1="272" y1="142" x2="314" y2="142" />
      <text className="hbB-soft" x="318" y="146">
        {en ? 'waist belt' : 'bederní pás'}
      </text>
      <line className="hbB-leader" x1="206" y1="199" x2="172" y2="199" />
      <text className="hbB-lbl" x="16" y="204">
        {en ? 'thigh straps' : 'stehenní popruhy'}
      </text>
      {/* pre-use inspection card (NV 362/2005 Sb., příl. čl. II) */}
      <g transform="translate(320 170)">
        <rect className="hbB-card" x="0" y="0" width="144" height="80" rx="6" />
        <text className="hbB-lbl" x="10" y="22">
          {en ? 'Before use' : 'Před použitím'}
        </text>
        <rect className="hbB-ok-fill" x="10" y="32" width="12" height="12" rx="2" />
        <text className="hbB-soft" x="30" y="43">
          {en ? 'webbing · seams' : 'popruhy · švy'}
        </text>
        <rect className="hbB-ok-fill" x="10" y="52" width="12" height="12" rx="2" />
        <text className="hbB-soft" x="30" y="63">
          {en ? 'buckles · ring' : 'spony · kroužek'}
        </text>
      </g>
    </Blueprint>
  );
}

/** Supine casualty + kneeling rescuer. Pins: shoulders (0.7, 0.56), chin/head tilt (0.8, 0.75), chest (0.56, 0.62). */
function UnconsciousCheckArt({ en }: { en: boolean }) {
  const ids = blueprintIds('unconsciousCheck');
  return (
    <Blueprint
      id="unconsciousCheck"
      label={en ? 'Checking an unconscious casualty' : 'Kontrola osoby v bezvědomí'}
      title={en ? 'Unconscious · check' : 'Bezvědomí · kontrola'}
      source="ERC 2025 · ČČK"
    >
      <Solid hatch={ids.hatch} x={40} y={232} width={400} height={10} edge="top" />
      {/* casualty supine, head to the right, chin lifted */}
      <g className="hbB-draw">
        <path d="M252 172 L342 162 L348 198 L256 212 Z" />
        <path d="M344 176 L362 184" />
        <circle cx="380" cy="192" r="18" />
        <path d="M252 176 L150 186 M256 212 L150 218 M150 186 L140 190 L140 222 L150 218" />
        <path d="M252 194 L150 202" />
        <path d="M330 178 L298 224 L270 228" />
      </g>
      {/* rib cage outline - where breathing shows */}
      <ellipse className="hbB-ghost" cx="290" cy="186" rx="42" ry="16" />
      {/* rescuer kneeling behind the casualty: one hand on the shoulder, one on the forehead, eyes on the chest */}
      <g className="hbB-draw">
        <circle cx="300" cy="92" r="15" />
        <path d="M290 108 L282 166 M312 106 L322 162" />
        <path d="M310 122 L336 150" />
        <path d="M314 118 L362 168 L372 178" />
        <circle cx="337" cy="153" r="4" />
      </g>
      <path className="hbB-ghost" d="M306 100 L278 176" />
      {/* head-tilt arc */}
      <path
        className="hbB-dim"
        d="M404 176 A30 30 0 0 1 418 202"
        markerEnd={`url(#${ids.arrow})`}
      />
      <text className="hbB-dim-txt" x="464" y="130" textAnchor="end">
        {en ? 'head tilt · chin lift' : 'záklon · zvednout bradu'}
      </text>
      <line className="hbB-leader" x1="424" y1="134" x2="404" y2="174" />
      {/* breathing check window */}
      <Dim arrow={ids.arrow} x1={250} y1={252} x2={352} y2={252} />
      <text className="hbB-dim-txt" x="340" y="268" textAnchor="middle">
        {en ? 'watch · max 10 s' : 'sleduj · max. 10 s'}
      </text>
      {/* procedure card */}
      <g transform="translate(16 36)">
        <rect className="hbB-card" x="0" y="0" width="204" height="92" rx="6" />
        <text className="hbB-mono" x="12" y="26">
          1
        </text>
        <text className="hbB-soft" x="30" y="26">
          {en ? 'speak · shake' : 'oslov · zatřes'}
        </text>
        <text className="hbB-mono" x="12" y="52">
          2
        </text>
        <text className="hbB-soft" x="30" y="52">
          {en ? 'tilt head · lift chin' : 'zakloň · zvedni bradu'}
        </text>
        <text className="hbB-mono" x="12" y="78">
          3
        </text>
        <text className="hbB-soft" x="30" y="78">
          {en ? 'chest · max 10 s' : 'hrudník · max. 10 s'}
        </text>
      </g>
      <text className="hbB-no-txt" x="16" y="156">
        {en ? 'not breathing → 155 · 30 : 2' : 'nedýchá → 155 · 30 : 2'}
      </text>
    </Blueprint>
  );
}

/** Flat interior with typical fire risks. Pins: extension lead (0.29, 0.78), blocked door (0.88, 0.59), solvent by the boiler (0.72, 0.75), smoke detector (0.63, 0.14). */
function FlatFireRisksArt({ en }: { en: boolean }) {
  const ids = blueprintIds('flatFireRisks');
  return (
    <Blueprint
      id="flatFireRisks"
      label={en ? 'Fire risks in a flat' : 'Požární rizika v bytě'}
      title={en ? 'Flat · fire risks' : 'Byt · požární rizika'}
      source="vyhl. 23/2008 Sb. · vyhl. 246/2001 Sb."
    >
      {/* room shell: ceiling slab, floor slab, side walls, door opening on the right */}
      <Solid hatch={ids.hatch} x={16} y={32} width={448} height={8} edge="bottom" />
      <Solid hatch={ids.hatch} x={16} y={238} width={448} height={12} edge="top" />
      <Solid hatch={ids.hatch} x={16} y={40} width={8} height={198} edge="right" />
      <Solid hatch={ids.hatch} x={446} y={40} width={18} height={52} edge="left" />
      <path className="hbB-draw-heavy" d="M398 238 V92 H446" />
      {/* smoke detector on the ceiling */}
      <path className="hbB-card" d="M288 40 a14 8 0 0 0 28 0 z" />
      <circle className="hbB-ok-fill" cx="302" cy="45" r="2.5" />
      {/* boiler on the wall with flue, flame window; solvent canister on the floor right beside it */}
      <rect className="hbB-thin" x="280" y="40" width="10" height="60" />
      <rect className="hbB-card" x="258" y="100" width="54" height="96" rx="4" />
      <rect className="hbB-draw" x="272" y="158" width="26" height="24" rx="2" />
      <path
        className="hbB-flame"
        d="M285 180 c-6 -4 -7 -10 -2 -14 c1 4 3 5 3 5 c-1 -6 3 -9 5 -11 c0 5 3 7 4 10 c2 6 -3 11 -10 10 z"
      />
      <text className="hbB-soft" x="294" y="96">
        {en ? 'boiler' : 'kotel'}
      </text>
      <rect className="hbB-cell" x="334" y="196" width="28" height="42" rx="3" />
      <rect className="hbB-draw" x="342" y="188" width="12" height="8" />
      {/* escape route through the door, stacked boxes in the opening */}
      <line
        className="hbB-ghost"
        x1="352"
        y1="124"
        x2="436"
        y2="124"
        markerEnd={`url(#${ids.arrow})`}
      />
      <text className="hbB-soft" x="352" y="114">
        {en ? 'exit' : 'únik'}
      </text>
      <rect className="hbB-cell" x="402" y="194" width="40" height="44" />
      <rect className="hbB-cell" x="408" y="150" width="32" height="44" />
      <path className="hbB-thin" d="M402 216 h40 M408 172 h32" />
      {/* wall socket + overloaded extension lead on the floor */}
      <rect className="hbB-draw" x="24" y="190" width="10" height="16" />
      <path className="hbB-draw" d="M34 198 q30 0 40 20 h30" />
      <rect className="hbB-card" x="104" y="210" width="70" height="16" rx="3" />
      <g className="hbB-draw">
        <circle cx="118" cy="218" r="4" />
        <circle cx="139" cy="218" r="4" />
        <circle cx="160" cy="218" r="4" />
        <path d="M118 210 L112 196 M139 210 L135 196 M160 210 L166 196" />
      </g>
      <path
        className="hbB-flame"
        d="M170 208 c-5 -3 -6 -8 -2 -11 c1 3 2 4 2 4 c-1 -5 2 -7 4 -9 c0 4 2 6 3 8 c2 5 -2 9 -8 8 z"
      />
      <text className="hbB-soft" x="36" y="184">
        {en ? '1 socket · 3 appliances' : '1 zásuvka · 3 spotřebiče'}
      </text>
      {/* legend - numbered like the pins */}
      <g transform="translate(36 52)">
        <rect className="hbB-card" x="0" y="0" width="212" height="104" rx="6" />
        <text className="hbB-mono" x="10" y="22">
          1
        </text>
        <rect className="hbB-no-fill" x="26" y="11" width="12" height="12" rx="2" />
        <text className="hbB-soft" x="46" y="22">
          {en ? 'overloaded lead' : 'přetížená prodlužovačka'}
        </text>
        <text className="hbB-mono" x="10" y="46">
          2
        </text>
        <rect className="hbB-no-fill" x="26" y="35" width="12" height="12" rx="2" />
        <text className="hbB-soft" x="46" y="46">
          {en ? 'blocked escape route' : 'zastavěná úniková cesta'}
        </text>
        <text className="hbB-mono" x="10" y="70">
          3
        </text>
        <rect className="hbB-no-fill" x="26" y="59" width="12" height="12" rx="2" />
        <text className="hbB-soft" x="46" y="70">
          {en ? 'solvent by the boiler' : 'hořlavina u kotle'}
        </text>
        <text className="hbB-mono" x="10" y="94">
          4
        </text>
        <rect className="hbB-ok-fill" x="26" y="83" width="12" height="12" rx="2" />
        <text className="hbB-soft" x="46" y="94">
          {en ? 'smoke detector' : 'hlásič kouře'}
        </text>
      </g>
    </Blueprint>
  );
}

export const SCENE_ART: Record<HotspotScene, (props: { en: boolean }) => JSX.Element> = {
  harnessPoints: HarnessPointsArt,
  unconsciousCheck: UnconsciousCheckArt,
  flatFireRisks: FlatFireRisksArt,
};

/**
 * Interactive engagement widget - numbered pins over a self-contained SVG
 * scene; tapping a pin reveals its label below the art and reports the first
 * tap via onInteract(block.id) (same contract as hotspotImage).
 */
export function HotspotSceneBlockView({
  block,
  locale,
  onInteract,
}: {
  block: HotspotSceneBlock;
  locale: LocaleCode;
  onInteract?: (widgetId: string) => void;
}) {
  const fired = useRef(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const en = locale === 'en-US';
  const Art = SCENE_ART[block.scene];
  const active = block.hotspots.find((hs) => hs.id === activeId);
  const activate = (id: string) => {
    setActiveId(id);
    if (fired.current) return;
    fired.current = true;
    onInteract?.(block.id);
  };
  return (
    <div data-block-type="hotspotScene" data-scene={block.scene}>
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: static module-local CSS constant, no user input */}
      <style dangerouslySetInnerHTML={{ __html: SCENE_CSS }} />
      <div data-hotspot-canvas style={{ position: 'relative' }}>
        <Art en={en} />
        {block.hotspots.map((hs, i) => (
          <button
            key={hs.id}
            type="button"
            data-hotspot-id={hs.id}
            aria-pressed={hs.id === activeId}
            aria-label={resolveLocalized(hs.label, locale)}
            onClick={() => activate(hs.id)}
            style={{ position: 'absolute', left: `${hs.x * 100}%`, top: `${hs.y * 100}%` }}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <p data-hotspot-caption data-empty={active ? undefined : 'true'}>
        {active
          ? resolveLocalized(active.label, locale)
          : en
            ? 'Tap the numbered points in the picture.'
            : 'Klepni na očíslované body v obrázku.'}
      </p>
    </div>
  );
}
